import React, {useCallback} from "react";
import {useForm} from "react-hook-form";
import {Subject} from "rxjs";
import {showToast} from "common";
import {useObservable} from "../../../../rxsupport";
import {ReportModal} from "../../form-components/ReportModal";
import {TreatmentHoldForm} from "./TreatmentHoldForm";
import {AuthorizationInfo} from "../../domain";
import {
    emptyTreatmentHoldInput,
    TreatmentHoldSubmitInput,
    fromApiTreatmentHold,
    shouldRedirectToStaffingPlan,
    toTreatmentHoldSubmitPayload,
} from "./treatmentHoldDefaults";

export type {TreatmentHoldSubmitInput};

export type TreatmentHoldInput = {
    holdType: string;
    requestReason: string;
    requestReasonOther: string;
    dateHoldDiscussedWithFamily: string;
    startDate: string;
    endDate: string;
    comments: string;
    canProviderResumeServices: string;
    canProviderOfferFso: string;
    providerResumeServices: string;
    familyNotifiedUnableToResume: string | null;
    dateOfLastTreatment: string | null;
    dateOfMostRecentAppointment: string | null;
    dateOfLastFullScheduleOffer: string | null;
    offeredParaprofessionalHours: number | null;
    offeredMidLevelHours: number | null;
    offeredHighLevelHours: number | null;
    offeredGroupCareHours: number | null;
    serviceType: string[];
    providerRequestForCcsConsultation: string | null;
    reasonForConsultation: string | null;
    summaryOfStepsTaken: string | null;
    providerContactName: string | null;
    providerContactEmail: string | null;
    providerContactPhone: string | null;
};

type Props = {
    visible$: Subject<boolean>;
    onFinished: (formValues: TreatmentHoldSubmitInput | null) => Promise<void>;
    authorizationGuid?: string;
    authorization?: AuthorizationInfo;
    onRedirectToStaffingPlan?: () => void;
    skipValidation?: boolean;
};

export function TreatmentHoldFormModal({
                                                 visible$,
                                                 onFinished,
                                                 authorizationGuid,
                                                 authorization,
                                                 onRedirectToStaffingPlan,
                                                 skipValidation = false,
                                             }: Props) {
    const visible = useObservable(visible$, false);

    const methods = useForm<TreatmentHoldInput>({
        defaultValues: emptyTreatmentHoldInput(authorization),
    });

    const handleFinished = useCallback(async (formValues: TreatmentHoldInput | null) => {
        if (formValues == null) {
            return;
        }
        const payload = toTreatmentHoldSubmitPayload(formValues, authorization);
        await onFinished(payload);
        if (shouldRedirectToStaffingPlan(formValues, authorization) && onRedirectToStaffingPlan) {
            onRedirectToStaffingPlan();
        }
    }, [onFinished, onRedirectToStaffingPlan, authorization]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Treatment Hold is cancelled`);
        methods.reset(emptyTreatmentHoldInput(authorization));
        visible$.next(false);
    }, [methods, visible$, authorization]);

    return (
        <ReportModal visible={visible} title={'Treatment Hold'} onClose={onCancel}>
            <TreatmentHoldForm
                methods={methods}
                visible$={visible$}
                onFinished={handleFinished}
                onCancel={onCancel}
                authorizationGuid={authorizationGuid}
                authorization={authorization}
                externalLoading={false}
                skipValidation={skipValidation}
            />
        </ReportModal>
    );
}
